import React, { useState } from 'react';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import theme from '../config/theme';

// Input Props interface
interface InputProps {
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url';
  id?: string;
  name?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  errorMessage?: string;
  label?: string;
  required?: boolean;
  isValid?: boolean;
  maxLength?: number;
  showPasswordToggle?: boolean;
}

// Input Component
const Input: React.FC<InputProps> = ({
  type = 'text',
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  className = '',
  error = false,
  errorMessage,
  label,
  required = false,
  isValid = true,
  maxLength,
  showPasswordToggle = false
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordType = type === 'password';
  const inputType = isPasswordType && showPasswordToggle ? (showPassword ? 'text' : 'password') : type;
  
  const baseClasses = 'w-full px-4 py-3 rounded-lg border font-light  focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200';
  const errorClasses = error ? 'text-gray-900' : 'text-gray-900';
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  
  // Add padding-right for password toggle button
  const paddingRight = showPasswordToggle && isPasswordType ? 'pr-12' : '';
  const inputClasses = `${baseClasses} ${errorClasses} ${disabledClasses} ${paddingRight} ${className}`;

  const inputStyle = {
    borderColor: error ? theme.color.error : theme.color.light500,
    backgroundColor: error ? `${theme.color.error}10` : theme.color.light100,
    color: theme.color.gray500,
    placeholderColor: theme.color.gray100,
    focusRingColor: theme.brandColor.primary,
    borderRadius: '0.5rem'
  };

  const togglePasswordVisibility = () => {
    if (!disabled) {
      setShowPassword(!showPassword);
    }
  };

  return (
    <div>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm mb-2"
          style={{ color: theme.color.gray400 }}
        >
          {label}
          {required && <span style={{ color: isValid ? theme.color.gray500 : theme.brandColor.red }} className="ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type={inputType}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          className={inputClasses}
          style={inputStyle}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          onFocus={(e) => {
            if (!disabled && !error) {
              e.target.style.border = `2px solid ${theme.brandColor.red}`;
              e.target.style.background = theme.color.light100;
              e.target.style.borderRadius = '0.5rem';
              e.target.style.boxShadow = `0 0 0 2px ${theme.brandColor.red}20`;
            }
          }}
          onBlur={(e) => {
            if (!disabled && !error) {
              e.target.style.border = `1px solid ${theme.color.light500}`;
              e.target.style.background = theme.color.light100;
              e.target.style.borderRadius = '0.5rem';
              e.target.style.boxShadow = 'none';
            }
            if (onBlur) {
              onBlur(e)
            }
          }}
        />
        {showPasswordToggle && isPasswordType && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            disabled={disabled}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: disabled ? theme.color.gray300 : theme.color.gray400 }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              React.createElement(IoEyeOffOutline as any, { className: "h-5 w-5" })
            ) : (
              React.createElement(IoEyeOutline as any, { className: "h-5 w-5" })
            )}
          </button>
        )}
      </div>
      {error && errorMessage && (
        <div
          className="mt-1 text-xs"
          style={{ color: theme.color.error }}
        >
          {errorMessage}
        </div>
      )}
    </div>
    
  );
};

export default Input;
